import {
  cidrOverlaps,
  ipInCidr,
  parseIpv4,
  parseIpv4Cidr,
  type Network,
  type NetworkRoute,
  type NetworkServer,
  type Subnet,
} from "@cloudserver/contracts";
import { useReadOnly } from "../api/users.js";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Route, Server, Shield, ShieldOff, Trash2, Unplug } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  progressOf,
  useAddRoute,
  useAddSubnet,
  useAttachServer,
  useDeleteNetwork,
  useDeleteRoute,
  useDeleteSubnet,
  useDetachServer,
  useNetwork,
  useNetworkProtection,
  useNetworkZones,
  useRunningOperations,
  useUpdateNetwork,
} from "../api/networks.js";
import { useServers } from "../api/servers.js";
import { ApiProblem } from "../api/client.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { EmptyState, PageHeader, Section, Tabs } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Badge, Button, Card, Spinner, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels, parseLabels } from "./labels.js";
import { deleteConsequences, failureToast, SelectField, subnetProblem } from "./networks.js";

export const NETWORK_TABS = ["resources", "subnets", "routes", "settings", "delete"] as const;
export type NetworkTab = (typeof NETWORK_TABS)[number];

const TAB_INTRO: Record<NetworkTab, string> = {
  resources: "networks.resourcesIntro",
  subnets: "networks.subnetsIntro",
  routes: "networks.routesIntro",
  settings: "networks.settingsIntro",
  delete: "networks.deleteIntro",
};

interface TabProps {
  projectId: string;
  network: Network;
  readOnly: boolean;
  /** Why changes are refused (guest or read-only token); empty when allowed. */
  readOnlyReason: string;
}

function AttachDialog({
  projectId,
  network,
  onClose,
}: {
  projectId: string;
  network: Network;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const servers = useServers(projectId);
  const attach = useAttachServer(projectId, network.id);
  const zones = new Set(network.subnets.filter((s) => s.type === "cloud").map((s) => s.zone));
  const attached = new Set(network.servers.map((s) => s.id));
  const candidates = (servers.data ?? []).filter((s) => !attached.has(s.id) && zones.has(s.zone));
  const [serverId, setServerId] = useState("");
  const [ip, setIp] = useState("");
  const chosen = candidates.find((s) => s.id === serverId) ?? candidates[0];

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!chosen) return;
    attach.mutate(
      { serverId: chosen.id, ...(ip.trim() ? { ip: ip.trim() } : {}) },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("networks.attached", { name: chosen.name }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("networks.attach")}
      description={t("networks.attachLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="attach-server" busy={attach.isPending} disabled={!chosen}>
            {t("networks.attachSubmit")}
          </Button>
        </>
      }
    >
      {servers.isPending ? (
        <Spinner label={t("common.loading")} />
      ) : !chosen ? (
        <Alert tone="neutral">{t("networks.attachNone")}</Alert>
      ) : (
        <form id="attach-server" onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <SelectField
            label={t("networks.attachServer")}
            name="server"
            value={chosen.id}
            onChange={setServerId}
          >
            {candidates.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.zone})
              </option>
            ))}
          </SelectField>
          <TextField
            label={t("networks.attachIp")}
            name="ip"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            spellCheck={false}
            placeholder={network.subnets.find((s) => s.zone === chosen.zone)?.ipRange}
            hint={t("networks.attachIpHint")}
            error={fieldError(attach.error, "ip", t) ?? fieldError(attach.error, "serverId", t)}
          />
          <ErrorMessage error={attach.error} />
        </form>
      )}
    </Dialog>
  );
}

function ResourcesTab({ projectId, network, readOnly, readOnlyReason }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const detach = useDetachServer(projectId, network.id);
  const [attaching, setAttaching] = useState(false);
  const [detaching, setDetaching] = useState<NetworkServer | null>(null);
  const attachButton = (
    <Button
      onClick={() => setAttaching(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("networks.attach")}
    </Button>
  );
  return (
    <>
      <Section title={t("networks.servers")} actions={network.servers.length ? attachButton : null}>
        <DataTable<NetworkServer>
          label={t("networks.servers")}
          rows={network.servers}
          rowKey={(s) => s.id}
          initialSort={{ key: "name", dir: "asc" }}
          empty={
            <EmptyState
              icon={<Server className="size-6" aria-hidden />}
              title={t("networks.noServers")}
              action={attachButton}
            >
              {t("networks.noServersLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("networks.attachServer"),
              primary: true,
              sort: (s) => s.name.toLowerCase(),
              cell: (s) => <span className="font-medium">{s.name}</span>,
            },
            {
              key: "ip",
              header: t("networks.privateIp"),
              sort: (s) => parseIpv4(s.ip) ?? 0,
              cell: (s) => <span className="font-mono text-xs">{s.ip}</span>,
            },
          ]}
          rowMenu={(s) => [
            [
              {
                label: t("networks.detach"),
                icon: <Unplug className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly,
                reason: readOnlyReason,
                onSelect: () => setDetaching(s),
              },
            ],
          ]}
        />
      </Section>
      {attaching && (
        <AttachDialog projectId={projectId} network={network} onClose={() => setAttaching(false)} />
      )}
      <DangerDialog
        open={detaching !== null}
        onOpenChange={(open) => !open && setDetaching(null)}
        title={t("networks.detachTitle", { name: detaching?.name ?? "" })}
        consequences={[
          t("networks.detachConsequence", { ip: detaching?.ip ?? "" }),
          t("networks.detachPublic"),
        ]}
        actionLabel={t("networks.detach")}
        busy={detach.isPending}
        onConfirm={() =>
          detaching &&
          detach.mutate(detaching.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("networks.detached", { name: detaching.name }) });
              setDetaching(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

function AddSubnetDialog({
  projectId,
  network,
  onClose,
}: {
  projectId: string;
  network: Network;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const zones = useNetworkZones(projectId);
  const add = useAddSubnet(projectId, network.id);
  const [range, setRange] = useState("");
  const [zone, setZone] = useState("");
  const [tried, setTried] = useState(false);
  const codes = zones.data?.map((z) => z.code) ?? [];
  const chosenZone = zone || network.subnets[0]?.zone || codes[0] || "eu-central";
  const inner = parseIpv4Cidr(range);
  const overlaps =
    inner !== null &&
    network.subnets.some((s) => {
      const other = parseIpv4Cidr(s.ipRange);
      return other !== null && cidrOverlaps(other, inner);
    });
  const problem =
    subnetProblem(network.ipRange, range) ?? (overlaps ? "networks.subnetOverlaps" : null);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (problem) return;
    add.mutate(
      { ipRange: range.trim(), zone: chosenZone },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("networks.subnetAdded", { range: range.trim() }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("networks.addSubnet")}
      description={t("networks.addSubnetLead", { range: network.ipRange })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="add-subnet" busy={add.isPending}>
            {t("networks.addSubnet")}
          </Button>
        </>
      }
    >
      <form id="add-subnet" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label={t("networks.subnetRange")}
          name="ipRange"
          value={range}
          onChange={(e) => setRange(e.target.value)}
          spellCheck={false}
          hint={t("networks.subnetRangeHint")}
          error={(tried && problem ? t(problem) : undefined) ?? fieldError(add.error, "ipRange", t)}
        />
        <SelectField
          label={t("networks.subnetZone")}
          name="zone"
          value={chosenZone}
          onChange={setZone}
          hint={t("networks.zoneHint")}
        >
          {(codes.length ? codes : [chosenZone]).map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </SelectField>
        <ErrorMessage error={add.error} />
      </form>
    </Dialog>
  );
}

function SubnetsTab({ projectId, network, readOnly, readOnlyReason }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useDeleteSubnet(projectId, network.id);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Subnet | null>(null);
  const busy = (s: Subnet) => {
    const cidr = parseIpv4Cidr(s.ipRange);
    return network.servers.some((server) => {
      const ip = parseIpv4(server.ip);
      return cidr !== null && ip !== null && ipInCidr(ip, cidr);
    });
  };
  return (
    <>
      <Section
        title={t("networks.subnets")}
        actions={
          <Button
            onClick={() => setAdding(true)}
            disabled={readOnly}
            icon={<Plus className="size-4" aria-hidden />}
          >
            {t("networks.addSubnet")}
          </Button>
        }
      >
        <DataTable<Subnet>
          label={t("networks.subnets")}
          rows={network.subnets}
          rowKey={(s) => s.ipRange}
          columns={[
            {
              key: "range",
              header: t("networks.subnetRange"),
              primary: true,
              cell: (s) => <span className="font-mono text-xs font-medium">{s.ipRange}</span>,
            },
            { key: "zone", header: t("networks.subnetZone"), cell: (s) => s.zone },
            {
              key: "type",
              header: t("networks.subnetType"),
              cell: (s) => t(`networks.subnetTypes.${s.type}`),
            },
            {
              key: "gateway",
              header: t("networks.gateway"),
              cell: (s) => <span className="font-mono text-xs text-muted">{s.gateway ?? "—"}</span>,
            },
          ]}
          rowMenu={(s) => [
            [
              {
                label: t("networks.deleteSubnet"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly || busy(s),
                reason: readOnly ? readOnlyReason : t("networks.subnetBusy"),
                onSelect: () => setDeleting(s),
              },
            ],
          ]}
        />
      </Section>
      {adding && (
        <AddSubnetDialog projectId={projectId} network={network} onClose={() => setAdding(false)} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("networks.deleteSubnetTitle", { range: deleting?.ipRange ?? "" })}
        consequences={[
          t("networks.deleteSubnetConsequence", { zone: deleting?.zone ?? "" }),
          t("networks.deleteSubnetInUse"),
        ]}
        actionLabel={t("networks.deleteSubnet")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.ipRange, {
            onSuccess: () => {
              toast({
                tone: "ok",
                title: t("networks.subnetDeleted", { range: deleting.ipRange }),
              });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

function AddRouteDialog({
  projectId,
  network,
  onClose,
}: {
  projectId: string;
  network: Network;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const add = useAddRoute(projectId, network.id);
  const [destination, setDestination] = useState("");
  const [gateway, setGateway] = useState("");
  const [tried, setTried] = useState(false);
  const range = parseIpv4Cidr(network.ipRange);
  const gatewayIp = parseIpv4(gateway.trim());
  const destinationError = parseIpv4Cidr(destination) ? null : "networks.subnetInvalid";
  const gatewayError =
    gatewayIp !== null && range !== null && ipInCidr(gatewayIp, range)
      ? null
      : "networks.gatewayInvalid";
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (destinationError || gatewayError) return;
    add.mutate(
      { destination: destination.trim(), gateway: gateway.trim() },
      {
        onSuccess: () => {
          toast({
            tone: "ok",
            title: t("networks.routeAdded", { destination: destination.trim() }),
          });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("networks.addRoute")}
      description={t("networks.addRouteLead", { range: network.ipRange })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="add-route" busy={add.isPending}>
            {t("networks.addRoute")}
          </Button>
        </>
      }
    >
      <form id="add-route" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label={t("networks.destination")}
          name="destination"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          spellCheck={false}
          hint={t("networks.destinationHint")}
          error={
            (tried && destinationError ? t(destinationError) : undefined) ??
            fieldError(add.error, "destination", t)
          }
        />
        <TextField
          label={t("networks.routeGateway")}
          name="gateway"
          value={gateway}
          onChange={(e) => setGateway(e.target.value)}
          spellCheck={false}
          hint={t("networks.routeGatewayHint")}
          error={
            (tried && gatewayError ? t(gatewayError) : undefined) ??
            fieldError(add.error, "gateway", t)
          }
        />
        <ErrorMessage error={add.error} />
      </form>
    </Dialog>
  );
}

function RoutesTab({ projectId, network, readOnly, readOnlyReason }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useDeleteRoute(projectId, network.id);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<NetworkRoute | null>(null);
  const addButton = (
    <Button
      onClick={() => setAdding(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("networks.addRoute")}
    </Button>
  );
  return (
    <>
      <Section title={t("networks.tabs.routes")} actions={network.routes.length ? addButton : null}>
        <DataTable<NetworkRoute>
          label={t("networks.tabs.routes")}
          rows={network.routes}
          rowKey={(r) => `${r.destination}>${r.gateway}`}
          empty={
            <EmptyState
              icon={<Route className="size-6" aria-hidden />}
              title={t("networks.noRoutes")}
              action={addButton}
            >
              {t("networks.noRoutesLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "destination",
              header: t("networks.destination"),
              primary: true,
              cell: (r) => <span className="font-mono text-xs font-medium">{r.destination}</span>,
            },
            {
              key: "gateway",
              header: t("networks.routeGateway"),
              cell: (r) => <span className="font-mono text-xs">{r.gateway}</span>,
            },
          ]}
          rowMenu={(r) => [
            [
              {
                label: t("networks.deleteRoute"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly,
                reason: readOnlyReason,
                onSelect: () => setDeleting(r),
              },
            ],
          ]}
        />
      </Section>
      {adding && (
        <AddRouteDialog projectId={projectId} network={network} onClose={() => setAdding(false)} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("networks.deleteRouteTitle", { destination: deleting?.destination ?? "" })}
        consequences={[
          t("networks.deleteRouteConsequence", {
            destination: deleting?.destination ?? "",
            gateway: deleting?.gateway ?? "",
          }),
        ]}
        actionLabel={t("networks.deleteRoute")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting, {
            onSuccess: () => {
              toast({
                tone: "ok",
                title: t("networks.routeDeleted", { destination: deleting.destination }),
              });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

/** Name and labels of a resource, each saved on its own (shared by networks and firewalls). */
export function NameAndLabels({
  name,
  labels,
  readOnly,
  saving,
  error,
  onSave,
}: {
  name: string;
  labels: Record<string, string>;
  readOnly: boolean;
  saving: boolean;
  error: unknown;
  onSave: (change: { name?: string; labels?: Record<string, string> }) => void;
}) {
  const { t } = useTranslation();
  const [labelsText, setLabelsText] = useState(formatLabels(labels));
  const [badLine, setBadLine] = useState<number | null>(null);
  const saveName = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({ name: String(new FormData(event.currentTarget).get("name") ?? "").trim() });
  };
  const saveLabels = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = parseLabels(labelsText);
    setBadLine(parsed.badLine);
    if (parsed.labels) onSave({ labels: parsed.labels });
  };
  return (
    <>
      <Card>
        <form onSubmit={saveName} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <TextField
            label={t("networks.rename")}
            name="name"
            defaultValue={name}
            required
            maxLength={128}
            className="flex-1"
            error={fieldError(error, "name", t)}
          />
          <Button type="submit" variant="secondary" disabled={readOnly} busy={saving}>
            {t("common.save")}
          </Button>
        </form>
      </Card>
      <Card>
        <form onSubmit={saveLabels} className="flex flex-col gap-3">
          <TextArea
            label={t("networks.labels")}
            name="labels"
            value={labelsText}
            onChange={(e) => setLabelsText(e.target.value)}
            rows={4}
            spellCheck={false}
            placeholder="env=production"
            hint={t("networks.labelsHint")}
            error={
              badLine !== null
                ? t("networks.labelsInvalid", { line: badLine })
                : fieldError(error, "labels", t)
            }
          />
          <div>
            <Button type="submit" variant="secondary" disabled={readOnly} busy={saving}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </Card>
      <ErrorMessage error={error} />
    </>
  );
}

function SettingsTab({ projectId, network, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateNetwork(projectId, network.id);
  const protection = useNetworkProtection(projectId, network.id);
  const on = network.protection.delete;
  return (
    <div className="flex flex-col gap-4">
      <NameAndLabels
        name={network.name}
        labels={network.labels}
        readOnly={readOnly}
        saving={update.isPending}
        error={update.error}
        onSave={(change) =>
          update.mutate(change, {
            onSuccess: () => toast({ tone: "ok", title: t("networks.saved") }),
          })
        }
      />
      <Card>
        <div className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{t("networks.protection")}</h2>
          <p className="text-sm text-muted">
            {on ? t("networks.protectionOn") : t("networks.protectionOff")}
          </p>
          <div>
            <Button
              variant="secondary"
              disabled={readOnly}
              busy={protection.isPending}
              icon={
                on ? (
                  <ShieldOff className="size-4" aria-hidden />
                ) : (
                  <Shield className="size-4" aria-hidden />
                )
              }
              onClick={() =>
                protection.mutate(!on, {
                  onSuccess: () => toast({ tone: "ok", title: t("networks.protectionChanging") }),
                  onError: (error) => toast(failureToast(t, error)),
                })
              }
            >
              {on ? t("networks.protectionDisable") : t("networks.protectionEnable")}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function DeleteTab({ projectId, network, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useDeleteNetwork(projectId);
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const blocked = network.protection.delete;
  return (
    <Card>
      <div className="flex flex-col gap-4">
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
          {deleteConsequences(t, network).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {blocked && (
          <Alert tone="warn">
            <p>{t("networks.deleteBlocked")}</p>
            <Link
              to="/projects/$projectId/networks/$networkId"
              params={{ projectId, networkId: network.id }}
              search={{ tab: "settings" }}
              className="mt-1 inline-block font-medium underline"
            >
              {t("networks.toSettings")}
            </Link>
          </Alert>
        )}
        <div>
          <Button
            variant="danger"
            disabled={readOnly || blocked}
            icon={<Trash2 className="size-4" aria-hidden />}
            onClick={() => setConfirming(true)}
          >
            {t("networks.delete")}
          </Button>
        </div>
      </div>
      <DangerDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("networks.deleteTitle", { name: network.name })}
        consequences={deleteConsequences(t, network)}
        confirmText={network.name}
        actionLabel={t("networks.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          // The page unmounts as soon as the copy no longer has the network, so
          // the follow-up must not depend on this component's mutate callbacks.
          void remove.mutateAsync(network.id).then(
            () => {
              toast({ tone: "ok", title: t("networks.deleted", { name: network.name }) });
              void navigate({ to: "/projects/$projectId/networks", params: { projectId } });
            },
            (error: unknown) => toast(failureToast(t, error)),
          )
        }
      />
    </Card>
  );
}

export function NetworkPage({
  projectId,
  networkId,
  tab,
}: {
  projectId: string;
  networkId: string;
  tab: NetworkTab;
}) {
  const { t } = useTranslation();
  const network = useNetwork(projectId, networkId);
  const running = useRunningOperations(projectId);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  const back = (
    <Link
      to="/projects/$projectId/networks"
      params={{ projectId }}
      className="inline-flex min-h-6 items-center gap-1 text-sm text-muted hover:text-text"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t("networks.back")}
    </Link>
  );

  if (network.error) {
    const gone = network.error instanceof ApiProblem && network.error.status === 404;
    return (
      <>
        <PageHeader title={t("nav.networks")} intro={gone ? t("networks.notFound") : undefined} />
        {back}
        {!gone && <ErrorMessage error={network.error} />}
      </>
    );
  }
  if (!network.data) return <Spinner label={t("common.loading")} />;
  const n = network.data;
  const progress = progressOf(running.data, n.id);
  const props = { projectId, network: n, readOnly, readOnlyReason };
  const views: Record<NetworkTab, ReactNode> = {
    resources: <ResourcesTab {...props} />,
    subnets: <SubnetsTab {...props} />,
    routes: <RoutesTab {...props} />,
    settings: <SettingsTab key={`${n.name}|${formatLabels(n.labels)}`} {...props} />,
    delete: <DeleteTab {...props} />,
  };

  return (
    <>
      {back}
      <PageHeader
        title={n.name}
        help="networks"
        meta={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono">{n.ipRange}</span>
            <Badge>
              {t("networks.servers")}: {n.servers.length}
            </Badge>
            {n.protection.delete && <Badge tone="ok">{t("networks.protected")}</Badge>}
          </div>
        }
        intro={t(TAB_INTRO[tab])}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {progress !== null && (
        <Alert tone="neutral">
          {t("networks.running")} {progress}%
        </Alert>
      )}
      <Tabs
        label={n.name}
        active={tab}
        items={NETWORK_TABS.map((key) => ({
          key,
          label: t(`networks.tabs.${key}`),
          link: {
            to: "/projects/$projectId/networks/$networkId",
            params: { projectId, networkId },
            search: { tab: key },
          },
        }))}
      />
      {views[tab]}
    </>
  );
}
